import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MailPlus, Loader2 } from "lucide-react";
import { BrandLogo, PenMark } from "./BrandLogo";
import { scrollToId } from "../data/books";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NewsletterBox = () => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("Parent");
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/newsletter`, { email, role });
      toast.success("You're on the list!", {
        description: "New-release news from Lil Momma & Man-Man is headed your way.",
      });
      setEmail("");
    } catch {
      toast.error("Hmm, that email didn't work", { description: "Check the address and try again." });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="mb-12 grid grid-cols-1 items-center gap-6 rounded-2xl border-2 border-banana/50 bg-cream/5 p-6 sm:p-8 md:grid-cols-2">
      <div>
        <p className="font-mono text-xs font-bold uppercase tracking-[0.3em] text-banana">Join the story</p>
        <p className="mt-2 font-display text-xl font-semibold leading-snug text-cream sm:text-2xl">
          Parents & teachers — be first to hear about new Lil Momma & Man-Man releases.
        </p>
      </div>
      <form data-testid="newsletter-form" onSubmit={submit} className="flex flex-col gap-3 sm:flex-row">
        <input
          data-testid="newsletter-input-email"
          required
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@example.com"
          className="w-full flex-1 rounded-xl border-2 border-cream/30 bg-ink px-4 py-3 text-base font-medium text-cream outline-none transition-colors placeholder:text-cream/40 focus:border-banana"
        />
        <select
          data-testid="newsletter-role-select"
          value={role}
          onChange={(e) => setRole(e.target.value)}
          className="rounded-xl border-2 border-cream/30 bg-ink px-4 py-3 text-base font-medium text-cream outline-none focus:border-banana"
        >
          {["Parent", "Teacher", "Librarian", "Fan"].map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        <button
          data-testid="newsletter-submit-btn"
          type="submit"
          disabled={sending}
          className="flex items-center justify-center gap-2 rounded-xl border-2 border-banana bg-banana px-5 py-3 font-head text-sm font-extrabold uppercase tracking-wide text-ink transition-all duration-200 hover:-translate-y-0.5 disabled:opacity-60"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <MailPlus className="h-4 w-4" />}
          Sign up
        </button>
      </form>
    </div>
  );
};

export default function Footer() {
  return (
    <footer data-testid="footer" className="halftone-light border-t-2 border-ink bg-ink py-14 text-cream">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <NewsletterBox />
        <div className="flex flex-col items-center gap-8 md:flex-row md:justify-between">
          <BrandLogo dark />
          <nav className="flex flex-wrap items-center justify-center gap-6">
            {[
              ["books", "The Books"],
              ["characters", "Meet the Cousins"],
              ["author", "Author"],
              ["contact", "Contact"],
            ].map(([id, label]) => (
              <button
                key={id}
                data-testid={`footer-link-${id}`}
                onClick={() => scrollToId(id)}
                className="font-head text-sm font-bold uppercase tracking-wide text-cream/60 transition-colors duration-200 hover:text-banana"
              >
                {label}
              </button>
            ))}
          </nav>
          <a
            data-testid="footer-amazon-link"
            href="https://www.amazon.com/s?k=Corella+Berry-Moten"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 rounded-full border-2 border-banana px-5 py-2 font-head text-sm font-extrabold uppercase tracking-wide text-banana transition-all duration-200 hover:-translate-y-0.5 hover:bg-banana hover:text-ink"
          >
            <PenMark className="h-4 w-4" /> Amazon
          </a>
        </div>
        <p className="mt-10 text-center font-mono text-xs uppercase tracking-widest text-cream/40">
          © 2026 Believe in Yourself series by Corella Berry-Moten. All rights reserved.
        </p>
      </div>
    </footer>
  );
}
